/**
 * <CreateAccountModal /> — Phase 4a Chunk 1 self-serve signup for the
 * Continuity System gateway tier.
 *
 * Uses the founder's exact button verbiage ("Create Account", never
 * "Sign Up") and finishes with a warm welcome screen before the payment
 * step (Chunk 2 will wire the "Continue to Payment" button to the existing
 * openSubscriptionModal event).
 *
 * Deliberately DOES NOT require a beta code or an invitation token — that
 * separation matters. This is the ONLY signup path that goes straight to
 * ``tier=portfolio_continuity``; Portfolio Clarity still routes through the
 * qualification-gated AccessRequestModal.
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, ShieldCheck, Sparkles } from 'lucide-react';
import COUNTRIES from '../../data/countries';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CreateAccountModal = ({ open, onClose, onSuccess }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [country, setCountry] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [welcome, setWelcome] = useState(null);

  const reset = () => {
    setFirstName(''); setLastName(''); setEmail(''); setPassword(''); setConfirm('');
    setCountry(''); setAgreed(false); setError(''); setWelcome(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!firstName.trim() || !lastName.trim() || !email || !password || !country) {
      setError('Please complete all fields marked required.');
      return;
    }
    if (password.length < 8 || !/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/[0-9]/.test(password) || !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      setError('Password must be at least 8 characters and include an uppercase letter, a lowercase letter, a number and a special character (like !@#$%).');
      return;
    }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (!agreed) { setError('Please accept the terms and privacy policy to continue.'); return; }
    setBusy(true);
    try {
      const fullName = `${firstName.trim()} ${lastName.trim()}`;
      const r = await fetch(`${BACKEND_URL}/api/auth/register/continuity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: fullName, email, password, country }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Account creation failed.');
      localStorage.setItem('auth_token', data.token);
      setWelcome({ name: data.user?.name || fullName, email: data.user?.email || email });
    } catch (err) {
      setError(err.message || 'Account creation failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleContinueToPayment = () => {
    reset();
    onClose?.();
    // Signal to useDashboard to auto-open the subscription modal on mount.
    // sessionStorage survives the react-router redirect (/dashboard →
    // /dashboard/overview) that would strip URL query params.
    sessionStorage.setItem('open_subscription_on_dashboard', '1');
    window.location.href = '/dashboard';
  };

  const handleClose = () => { reset(); onClose?.(); };

  // "I'll do this later" — user signed up but skips payment for now. They're
  // logged in but their subscription_status remains pending_payment. Send
  // them to the dashboard cleanly so they can pay whenever they return.
  const handleLaterSkip = () => {
    reset();
    onClose?.();
    window.location.href = '/dashboard';
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="create-account-modal">
        <DialogTitle className="sr-only">Create your Continuity System account</DialogTitle>
        {!welcome ? (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="create-account-form">
            <div className="flex items-center gap-3 pb-2">
              <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-2 shrink-0">
                <ShieldCheck className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <h2 className="text-lg font-serif font-semibold text-slate-900">
                  Create your Continuity System account
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">Takes about a minute.</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ca-first-name">First name</Label>
                <Input id="ca-first-name" data-testid="create-first-name" value={firstName}
                       onChange={(e) => setFirstName(e.target.value)} placeholder="Ada" autoComplete="given-name" required />
              </div>
              <div>
                <Label htmlFor="ca-last-name">Last name</Label>
                <Input id="ca-last-name" data-testid="create-last-name" value={lastName}
                       onChange={(e) => setLastName(e.target.value)} placeholder="Lovelace" autoComplete="family-name" required />
              </div>
            </div>
            <div>
              <Label htmlFor="ca-email">Email</Label>
              <Input id="ca-email" data-testid="create-email" type="email" value={email}
                     onChange={(e) => setEmail(e.target.value.toLowerCase())} placeholder="ada@example.com" required />
            </div>
            <div>
              <Label htmlFor="ca-country">Country</Label>
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger id="ca-country" data-testid="create-country">
                  <SelectValue placeholder="Select your country" />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="ca-password">Password</Label>
              <Input id="ca-password" data-testid="create-password" type="password" value={password}
                     onChange={(e) => setPassword(e.target.value)} required />
              <p className="text-[11px] text-slate-500 mt-1">
                Must be at least 8 characters and include an uppercase letter, a lowercase letter, a number and a special character (like !@#$%).
              </p>
            </div>
            <div>
              <Label htmlFor="ca-confirm">Confirm password</Label>
              <Input id="ca-confirm" data-testid="create-confirm" type="password" value={confirm}
                     onChange={(e) => setConfirm(e.target.value)} required />
            </div>

            <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
              <input type="checkbox" data-testid="create-terms" checked={agreed}
                     onChange={(e) => setAgreed(e.target.checked)}
                     className="mt-0.5 accent-emerald-600" />
              <span>
                I agree to the <a href="/terms" target="_blank" rel="noopener" className="underline text-emerald-700">Terms</a>
                {' '}and <a href="/privacy" target="_blank" rel="noopener" className="underline text-emerald-700">Privacy Policy</a>.
              </span>
            </label>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md p-2" data-testid="create-error">
                {error}
              </div>
            )}

            <Button type="submit" data-testid="create-account-submit" disabled={busy}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-full py-3">
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</> : 'Create Account'}
            </Button>
            <p className="text-[11px] text-slate-500 text-center">
              You&apos;ll be asked for payment on the next screen. No charge until you confirm.
            </p>
          </form>
        ) : (
          <div className="space-y-5 text-center py-2" data-testid="create-account-welcome">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
              <Sparkles className="w-7 h-7 text-emerald-700" />
            </div>
            <h2 className="text-xl font-serif font-semibold text-slate-900">
              Welcome, {welcome.name.split(' ')[0]}!
            </h2>
            <p className="text-sm text-slate-600 max-w-sm mx-auto leading-relaxed">
              Your Continuity System account is ready. One more step —
              confirm your subscription so we can activate your encrypted Continuity System
              and set up your secure record.
            </p>
            <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 text-left">
              <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Signed in as</p>
              <p className="text-sm text-slate-900 mt-1">{welcome.email}</p>
            </div>
            <Button onClick={handleContinueToPayment} data-testid="continue-to-payment-btn"
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-full py-3">
              Continue to Payment →
            </Button>
            <button type="button" onClick={handleLaterSkip}
                    className="text-xs text-slate-500 hover:text-slate-700 underline underline-offset-2"
                    data-testid="welcome-later-link">
              I&apos;ll do this later
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreateAccountModal;
